import React, { useState, useCallback, memo, useEffect, useContext } from 'react'
import { Transaction } from '@odiupsk/up-api-client'
import { observer } from 'mobx-react-lite'
import { trace } from 'mobx'
import { Trans } from '@lingui/macro'
import cx from 'classnames'
import Link from 'next/link'
import { Grid, GridCol, Bar, BarItem, Table, ButtonLink, Column, Icon } from '../../common/ui-kit'
import { SearchBar, SearchBarProps } from '../../common/modules/data-table/filters/SearchBar'
import {
  DateIntervalFilter,
  DateIntervalFilterProps,
} from '../../common/modules/data-table/filters/DateIntervalFilter'
import {
  TransactionTypesFilter,
  TransactionTypesFilterProps,
} from '../../common/modules/data-table/filters/TransactionTypesFilter'
import {
  PlacesFilter,
  PlacesFilterProps,
} from '../../common/modules/data-table/filters/PlacesFilter'
import { PriceFilter, PriceFilterProps } from '../../common/modules/data-table/filters/PriceFilter'
import { TablePagination } from '../../common/modules/data-table/TablePagination'
import { TransactionDetailModal } from './TransactionDetailModal'
import { TTransactionTableRow } from './TransactionsTableStore'
import { i18n } from '../../common/utils/intl'
import { formatDateTime, formatMoney } from '../../common/utils/format'
import { PageDashboardCtx } from './PageDashboard'
import { WidgetNoDataContainer } from '../../common/modules/data-table/WidgetNoDataContainer'
import { useDialog } from '../../common/utils/hooks/useDialog'
import { SCOPES, URLS } from '../../config/constants'
import { createSearchFilter } from '../../common/utils/filterExpressionBuilder'
import { appStore } from '../../common/AppStore'

const TableActions = memo(
  (
    props: DateIntervalFilterProps &
      TransactionTypesFilterProps &
      PlacesFilterProps &
      PriceFilterProps &
      Pick<SearchBarProps, 'onSearchClick'> & {
        onResetFilters?(): void
        filtersDirty: boolean
        loading: boolean
      }
  ) => {
    return (
      <Grid>
        <GridCol equalizeHeight size={{ m: 'fill' }}>
          <SearchBar
            placeholder={i18n.t`Hľadať v transakciách`}
            onSearchClick={props.onSearchClick}
            loading={props.loading}
            onChange={term => {
              if (term === '') props.onSearchClick?.()
            }}
          />
        </GridCol>

        <GridCol size={{ m: 10 }}>
          <Bar align="left">
            <BarItem className="text-uppercase label small-bar-item">
              <Trans>Filter</Trans>:
            </BarItem>
            <BarItem className="small-bar-item">
              <DateIntervalFilter onDateChange={props.onDateChange} />
            </BarItem>
            <BarItem className="small-bar-item">
              <TransactionTypesFilter onTransactionTypeChange={props.onTransactionTypeChange} />
            </BarItem>
            {!appStore.hasIdInSomeScope([SCOPES.Holder]) && (
              <BarItem className="small-bar-item">
                <PlacesFilter onPlaceTypeChange={props.onPlaceTypeChange} />
              </BarItem>
            )}

            <BarItem className="small-bar-item">
              <PriceFilter onPriceChange={props.onPriceChange} />
            </BarItem>
          </Bar>
        </GridCol>
        <GridCol size={{ m: 2 }}>
          {props.filtersDirty && (
            <Bar align="right">
              <BarItem style={{ marginRight: '0' }} className="small-bar-item">
                <ButtonLink space="condensed" size="small" onClick={props.onResetFilters}>
                  <Trans>Resetovať filter</Trans>
                  <Icon className="icon--right" name="close" size="medium" />
                </ButtonLink>
              </BarItem>
            </Bar>
          )}
        </GridCol>
      </Grid>
    )
  }
)

const TransactionDetail: React.FC<{
  transaction: Transaction
}> = observer(({ transaction }) => {
  const d = useDialog()

  return (
    <Bar className="align-items-right mb-none" nowrap>
      <BarItem>
        <ButtonLink space="condensed" size="tiny" className="mb-none" onClick={d.open}>
          <Trans>Detail</Trans>
        </ButtonLink>
      </BarItem>
      {d.isOpen && <TransactionDetailModal transaction={transaction} close={d.close} />}
    </Bar>
  )
})

const tableColumns: Column<TTransactionTableRow>[] = [
  {
    key: 'date_date',
    label: i18n.t`Dátum a čas`,
    thClasses: 'table__col--auto text-nowrap',
    tdClasses: 'table__col--shrink text-nowrap',
    render(data) {
      return (
        <span className={cx(data.isStorno && 'text-color-grey-300')}>
          {formatDateTime(data.date_date!)}
        </span>
      )
    },
  },
  {
    key: 'transaction_type.name',
    label: i18n.t`Typ`,
    thClasses: 'table__col--auto text-nowrap',
    tdClasses: 'table__col--shrink text-bold',
    render(data) {
      return (
        <span className={cx(data.isStorno && 'text-color-grey-300')}>
          {data.transaction_type.name}
        </span>
      )
    },
  },
  {
    key: 'main_description',
    label: i18n.t`Popis`,
    thClasses: 'table__col--auto text-nowrap',
    tdClasses: 'table__col--auto text-ellipsis',
    render(data) {
      return (
        <span className={cx(data.isStorno && 'text-color-grey-300')}>{data.main_description}</span>
      )
    },
  },
  {
    key: 'amount',
    label: i18n.t`Suma`,
    thClasses: 'table__col--auto text-nowrap',
    tdClasses: 'table__col--shrink text-ellipsis text-bold text-right',
    render(data) {
      const amount = Number(data.amount)
      return (
        <span
          className={cx(
            data.isStorno && 'text-color-grey-300',
            data.isAccepted && amount < 0 && 'text-color-turquoise-600',
            data.notAccepted && 'text-color-error'
          )}>
          {formatMoney(amount)} {data.raw.currency?.name}
        </span>
      )
    },
  },
  {
    key: 'actions',
    label: i18n.t`Akcie`,
    thClasses: 'text-center',
    tdClasses: 'table__col--shrink text-right',
    noSort: true,
    render(row) {
      return <TransactionDetail transaction={row.raw} />
    },
  },
]

export const TransactionsTable = observer(() => {
  trace()

  const { storeTransactions: store, storeCards: cardsStore } = useContext(PageDashboardCtx)
  store.selectedCardId = cardsStore.selectedCardId

  // by changing this value used as `key` for filters - we reset them
  const [actionsKey, setActionKey] = useState('actions')

  useEffect(() => {
    if (!cardsStore.selectedCardId) return
    store.fetchTransactions(store.params)
  }, [cardsStore.selectedCardId, store])

  useEffect(() => {
    store.fetchTransactions(store.params)
  }, [store.watch, store])

  const onSearch = useCallback(
    (term?: string) => {
      if (!term || term === '') {
        store.filters.setFilter('search', '')
      } else {
        store.filters.setFilter(
          'search',
          createSearchFilter(['main_description', 'transaction_type.name'], term ?? '')
        )
      }
    },
    [store]
  )

  const onDateChange = useCallback(
    (args?: { from: Date; to: Date }) => {
      store.filters.setFilter(
        'date_date',
        args ? `date_date>=${args.from.toISOString()}&&date_date<=${args.to.toISOString()}` : ''
      )
    },
    [store]
  )

  const onPlaceTypeChange = useCallback(
    (term?: string) => {
      // TODO: really code?
      store.filters.setFilter('place', term ? `merchant.types.id=${term}` : '')
    },
    [store]
  )

  const onPriceChange = useCallback(
    (args?: { from: number; to: number }) => {
      store.filters.setFilter(
        'price',
        args ? `amount_abs>=${args.from}&&amount_abs<=${args.to}` : ''
      )
    },
    [store]
  )

  const onTransactionTypeChange = useCallback(
    (type?: string) => {
      store.filters.setFilter('transactionType', type ? `transaction_type.id=${type}` : '')
    },
    [store]
  )

  const onResetFilters = useCallback(() => {
    store.filters.clearAll()
    setActionKey(s => `${s}1`)
  }, [store, setActionKey])

  const emptyTableMarkup = (
    <div>
      <div className="mb-large" />
      <p className="text-color-grey">
        <Trans>Nevykonali ste zatiaľ žiadne transakcie.</Trans>
      </p>
      <Link href={URLS.acceptationPlaces({})}>
        <a className="link">
          <Trans>Pozrite sa kde sa dá vaša Up Karta použiť.</Trans>
        </a>
      </Link>
      <div className="mb-large" />
    </div>
  )

  return (
    <React.Fragment>
      <h2 className="h5">
        <Trans>Transakcie na karte</Trans>
      </h2>
      {cardsStore.cards.length > 1 && (
        <div className="text-small mb-tiny" style={{ visibility: 'hidden' }}>
          invisible
        </div>
      )}
      <TableActions
        key={actionsKey}
        onSearchClick={onSearch}
        onDateChange={onDateChange}
        onPlaceTypeChange={onPlaceTypeChange}
        onPriceChange={onPriceChange}
        onTransactionTypeChange={onTransactionTypeChange}
        onResetFilters={onResetFilters}
        filtersDirty={store.filters.isDirty}
        loading={store.loading}
      />
      <Table<TTransactionTableRow>
        sortable
        rowKey="id"
        columns={tableColumns}
        data={store.data}
        sortedColumns={store.sorting.sortDefsAsRecord}
        sortColumn={val => store.sorting.setColumnSortDir(val)}
        emptyTableMarkup={store.isLoaded ? emptyTableMarkup : ''}
        {...(store.isFilteredAndNoData && {
          emptyTableMarkup: (
            <WidgetNoDataContainer>
              <Trans>Danému filtru nevyhovujú žiadne tranzakcie</Trans>
            </WidgetNoDataContainer>
          ),
        })}
      />
      <TablePagination store={store.pagination} />
      <div className="mb-large" />
    </React.Fragment>
  )
})
